import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Container,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppColors } from "../../../../util/AppColors";

// Import Thunks
import CustomButton from "../../../../components/CustomButton";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomSwitch from "../../../../components/CustomSwitch";
import CustomTable from "../../../../components/CustomTable";
import CustomTextField from "../../../../components/CustomTextField";
import TopBar from "../../../../components/TopBar/TopBar";
import {
  createKennel,
  deleteKennel,
  getKennelList,
  updateKennel,
} from "../../../../redux/reducers/kennelSlice";

const tblHdrs = [
  "sNo",
  "kennel",
  "newStatus",
  "createdBy",
  "createdDate",
  "actions",
];

function Kennel() {
  const dispatch = useDispatch();

  const [search, setSearch] = useState("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState({ Kennel: "" });
  const [editForm, setEditForm] = useState({ Kennel: "" });
  // Add states
  const [delModVisible, setDelModVisible] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const kennelState = useSelector((state) => state.kennel);
  const kennelList = kennelState.list;

  useEffect(() => {
    dispatch(getKennelList());
  }, [dispatch]);

  const handleAddOrUpdate = () => {
    if (!form.Kennel.trim()) return;

    if (editIndex !== null) {
      const kennel = kennelList[editIndex];
      dispatch(
        updateKennel({ facilityId: kennel.facilityId, name: form.Kennel })
      );
    } else {
      dispatch(createKennel({ name: form.Kennel }));
    }

    handleCloseDrawer();
  };
  const handleDeleteClick = (index) => {
    setDeleteIndex(index);
    setDelModVisible(true);
  };

  const handleDelModClose = () => {
    setDelModVisible(false);
    setDeleteIndex(null);
  };

  const handleDelConfirm = () => {
    const kennel = kennelList[deleteIndex];
    dispatch(deleteKennel({ facilityId: kennel.facilityId }));
    setDelModVisible(false);
    setDeleteIndex(null);
  };

  const handleEdit = (index) => {
    const kennel = kennelList[index];
    setForm({ Kennel: kennel.name });
    setEditIndex(index);
    setOpenDrawer(true);
  };
  const handleUpdate = () => {
    if (!editForm.Kennel.trim()) return;

    const kennel = kennelList[editIndex];
    dispatch(
      updateKennel({ facilityId: kennel.facilityId, name: editForm.Kennel })
    )
      .unwrap()
      .then(() => {
        handleCloseEditDialog(); // Close dialog after update
      })
      .catch((error) => {
        console.error("Failed to update Kennel:", error);
      });
  };

  const handleDelete = (index) => {
    const kennel = kennelList[index];
    dispatch(deleteKennel({ facilityId: kennel.facilityId }));
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setForm({ Kennel: "" });
    setEditIndex(null);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditForm({ Kennel: "" });
    setEditIndex(null);
  };

  const filteredRows = kennelList.filter((row) =>
    row.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleChangeSwitch = (status, kennel) => {
    dispatch(
      updateKennel({
        facilityId: kennel?.facilityId,
        name: kennel?.name,
        status,
      })
    ).then((res) => {
      if (res?.payload) dispatch(getKennelList());
    });
  };

  return (
    <Box>
      {/* Top section */}
      <TopBar>
        <Container maxWidth="xl">
          <Toolbar variant="regular">
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex-row-ali-cen">
                <div className="top-row-cen-con w100Per">
                  <div className="w30Per">
                    <CustomTextField
                      placeholder="Search"
                      name="name"
                      fullWidth
                      handleChange={(e) => setSearch(e.target.value)}
                      value={search}
                      backgroundColor={AppColors.lightBlue2}
                    />
                  </div>

                  <div className="ml20">
                    <CustomButton
                      text="Create"
                      onClick={() => setOpenDrawer(true)}
                    />
                  </div>
                </div>
              </div>
            </Grid>
          </Toolbar>
          <Typography className="font-medium fs12 gray28">
            Total Kennels: {filteredRows?.length}
          </Typography>
        </Container>
      </TopBar>

      {/* Table */}
      <CustomTable
        grey
        columns={tblHdrs}
        datas={filteredRows?.map((kennel, index) => ({
          sNo: index + 1,
          kennel: kennel?.name,
          newStatus: (
            <div>
              <div>
                <CustomSwitch
                  value={kennel?.status}
                  onChange={(e) => handleChangeSwitch(e?.target?.value, kennel)}
                  greenToRed
                />
              </div>
              <div>{kennel?.status ? "Active" : "Under Maintenance"}</div>
            </div>
          ),
          createdBy: kennel?.userName || "-",
          createdDate: new Date(kennel?.createdDate)?.toLocaleDateString(),
          actions: (
            <div>
              <IconButton
                color="primary"
                size="small"
                onClick={() => handleEdit(index)}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                color="error"
                size="small"
                onClick={() => handleDeleteClick(index)}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          ),
        }))}
      />

      {/* Drawer */}
      <Drawer anchor="right" open={openDrawer} onClose={handleCloseDrawer}>
        <Box p={3} width={300}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" color="primary">
              {editIndex !== null ? "Edit Kennel" : "Create Kennel"}
            </Typography>
            <IconButton onClick={handleCloseDrawer} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <TextField
            fullWidth
            label="Kennel Name"
            value={form.Kennel}
            onChange={(e) => setForm({ ...form, Kennel: e.target.value })}
            margin="normal"
            InputLabelProps={{
              sx: {
                position: "absolute",
                top: "50%",
                left: "14px",
                transform: "translateY(-50%)",
                transition: "all 0.2s ease-out",
                "&.Mui-focused, &.MuiFormLabel-filled": {
                  top: "0px",
                  transform: "translateY(-50%) scale(0.75)",
                  transformOrigin: "top left",
                },
              },
            }}
          />
          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleAddOrUpdate}
          >
            {editIndex !== null ? "Update" : "Add"}
          </Button>
        </Box>
      </Drawer>

      {/* Edit Dialog */}
      <CustomModal
        open={delModVisible}
        onClose={handleDelModClose}
        header="Delete"
        modal
        modalWidth={30}
      >
        <Typography className="flex-center txt-regular fs14">
          Are you sure you want to delete this record?
        </Typography>

        <div className="flex-center mb10 mt15">
          <div>
            <CustomButton text="Cancel" onClick={handleDelModClose} />
          </div>
          <div className="ml10">
            <CustomButton redBtn text="Confirm" onClick={handleDelConfirm} />
          </div>
        </div>
      </CustomModal>
    </Box>
  );
}

export default Kennel;
